import { useMutation } from "@apollo/client";
import dynamic from "next/dynamic";
import * as Sentry from "@sentry/nextjs";
import { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Head from "next/head";
import Link from "next/link";
import { StarIcon } from "@heroicons/react/24/solid";
import { useForm } from "react-hook-form";
import { useRouter } from "next/router";

import { COOKIE } from "../../utils/keys";
import ApiClient from "../../utils/client";
import { faqTemplate, serviceTemplate } from "../../utils/schema";

import { serviceByUuid } from "../../queries/services";
import { klayAction } from "../../utils/formats";

import Phone from "../../assets/phone.svg";
import { Conversion, recordConversion } from "../../utils/conversions";
import { trackAction } from "../../utils/analytics";
import { useSession } from "../../providers/session";
import cookies from "js-cookie";
import { GetStaticProps } from "next/types";
import Spinner from "../../assets/spinner.svg";
import { newOrder } from "../../queries/appointments";
import { classNames } from "../../utils/classnames";

import EmbeddedDetails from "../../components/organisms/checkout/embedded-details";

const EmbeddedAppointmentSelector = dynamic(
  () => import("../../components/organisms/checkout/embedded-calender")
);
const Layout = dynamic(() => import("../../components/layout"));
const Markdown = dynamic(() => import("../../components/molecules/markdown"));
const Modal = dynamic(() => import("../../components/organisms/modal"));
const Klarna = dynamic(() => import("../../components/klarna"), {
  ssr: false,
});

const offers = [
  {
    name: "Laser Hair Removal",
    description: "Get up to 80% off courses",
    href: "/laser-hair-removal/",
  },
  {
    name: "Treatment Plans",
    description: "Save On Individual Sessions With Plans",
    href: "/price-list/",
  },
  {
    name: "Price Match Guarantee",
    description: "Unbeatable Prices Guaranteed",
    href: "/price-list/",
  },
];

const offers2 = [
  {
    name: "Skin Analysis",
    description: "SkinScope LED/UV Diagnosis Test",
  },
  {
    name: "Luxury Facials",
    description: "Industry Leading Skincare Recommendations",
  },
  {
    name: "Special Offers",
    description: "15% Off Your First Treatment Plan",
  },
];

export type ConsultationProps = {
  item: any;
  option: string;
  date: Date;
  time: string;
  quantity: number;
  resource: string;
};

export default function Variant(props) {
  const detailsRef = useRef(null);
  console.log('props', props);
  let consultation: ConsultationProps = {
    item: props.facialContent,
    option: props.facialContent?.pricing[0]?.data?.id,
    quantity: 1,
    time: null,
    date: null,
    resource: "9ccecc77-2a14-4937-9584-f0a153328da8",
  };
  const router = useRouter();
  const [createOrder] = useMutation(newOrder);

  const service = props.facialContent;
  const { profile, sourceParams } = useSession();
  const [state, setState] = useState({
    loading: false,
    error: false,
    errorMessage: null,
    success: false,
    callback: false,
    open: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    try {
      // @ts-ignore
      window.gtag &&
        window.gtag("set", {
          experiments: [
            { id: props.variant.id, variant: props.variant.version },
          ],
        });

      const item = klayAction({
        service,
        type: "view",
      });

      // @ts-ignore
      klaviyo && klaviyo.push(["track", "Viewed Product", item]);
      // @ts-ignore
      klaviyo && klaviyo.push(["trackViewedItem", item]);

      window.gtag &&
        window.gtag("event", "view_item", {
          currency: "GBP",
          value: service.pricing[service.pricing.length - 1].data.price / 100,
          items: [
            {
              id: service.id,
              item_id: service.id,
              name: service.title,
              item_name: service.title,
              price:
                service.pricing[service.pricing.length - 1].data.price / 100,
            },
          ],
        });
    } catch (err) {
      console.log("tracking error", err);
      Sentry.captureException(err);
    }
  }, []);

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  const meta = serviceTemplate(service);
  let faqMeta = null;
  if (service.content.faqs) {
    faqMeta = faqTemplate(service.content.faqs);
  }

  const fromPrice =
    service.pricing[
      service.pricing.findIndex((x) => x.data.label !== "Booking Fee")
    ];

  const handle = async (data) => {
    if (state.loading) {
      return false;
    }
    setState({
      ...state,
      loading: true,
      error: null,
    });

    try {
      const body = JSON.stringify({
        ...data,
        first_name: data.first_name.replace(/\s/g, ""),
        last_name: data.last_name.replace(/\s/g, ""),
        email: data.email.replace(/\s/g, "").toLowerCase(),
        phone: data.phone.replace(/\s/g, ""),
        subject: `${service.title} Callback Request`,
        message: `A new ${service.title} callback request has been made for ${data.first_name}.`,
        sourceParams,
        attributes: {
          id: service.id,
          url: service.metadata.path,
          title: service.title,
        },
      });

      const r = await fetch("/api/callback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });

      const t = await r.json();

      trackAction("Callback");
      recordConversion({
        send_to: Conversion.callback,
        // @ts-ignore
        value: 1,
        currency: "GBP",
      });
      (window as any).heap &&
        (window as any).heap.track("Callback Request", {
          method: "Callback Request",
          service: service.title,
          ...data,
        });

      try {
        const item = klayAction({
          service: props.content[0],
          type: "view",
        });

        // @ts-ignore
        klaviyo &&
          // @ts-ignore
          klaviyo.push([
            "identify",
            {
              $email: data.email,
              $first_name: data.first_name,
              $last_name: data.last_name,
            },
          ]);

        // @ts-ignore
        klaviyo &&
          // @ts-ignore
          klaviyo.push([
            "track",
            "Callback Request",
            {
              ...item,
              $email: data.email,
              $first_name: data.first_name,
              $last_name: data.last_name,
              $phone_number: data.phone,
            },
          ]);
      } catch (err) {
        console.log(err);
        Sentry.captureException(err);
      }

      setState({
        ...state,
        loading: false,
        error: null,
        success: true,
        callback: false,
      });
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
      setState({
        ...state,
        error: true,
        loading: false,
      });
    }
  };

  // dde4c697-87e6-4d26-a8ca-48d433bd4e1f -> face
  // 147ca6ad-1a55-40b4-af5c-00facd5c154b -> body
  const handleBooking = async (consultation) => {
    setState({
      ...state,
      // loading: true,
    });
    if (state.loading) {
      return;
    }
    let detailsValid = false;

    if (detailsRef.current) {
      detailsValid = await detailsRef.current.valid();
    }

    const personalDetails = await detailsRef.current.getValues();

    if (consultation.type && consultation.type === 'Body') {
      consultation.item = props.bodyContent,
      consultation.option = props.bodyContent?.pricing[0]?.data?.id
    } else {
      consultation.item = props.facialContent,
      consultation.option = props.facialContent?.pricing[0]?.data?.id
    }

    const validDetails = consultation.date && consultation.time && detailsValid;
    let result;

    let cookieData = {};
    try {
      cookieData = JSON.parse(cookies.get(COOKIE));
    } catch (err) {
      console.log(err);
    }

    const items = { products: [], services: [consultation] };

    const orderData = {
      user: profile ? profile.id : null,
      data: {
        items,
        personalDetails,
        version: 2,
        metadata: {
          ...sourceParams,
          ...cookieData,
        },
      },
    };

    if (validDetails) {
      result = await createOrder({
        variables: {
          data: orderData,
        },
      });
    } else {
      setState({
        ...state,
        loading: false,
        error: true,
        errorMessage: "Please Make Sure All Information Entered Is Correct",
      });
    }
    if (result) {
      const res = await fetch(`/api/book/`, {
        method: "POST",
        body: JSON.stringify({
          orderId: result.data.order.returning[0].id,
        }),
      });
      router.push(`/thanks/${result.data.order.returning[0].id}`);
    } else {
      const x = document.getElementsByClassName("error")[0];
      x && x.scrollIntoView({ behavior: "smooth" });
      setState({
        ...state,
        loading: false,
        error: true,
        errorMessage:
          "Please complete all required fields and agree to the terms and conditions of booking",
      });
    }
  };

  return (
    <>
      <Layout>
        <Head>
          <title>Book A Free No Obligation Consultation at Soluna London</title>
          <link
            rel="canonical"
            href={`https://solunalondon.com/consultations/`}
          />
          <meta name="description" content={service.metadata.description} />
          <link
            rel="alternate"
            hrefLang="en-GB"
            href={`https://solunalondon.com/consultations/`}
          />
          <link
            rel="alternate"
            hrefLang="en"
            href={`https://solunalondon.com/consultations/`}
          />
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`https://solunalondon.com/consultations/`}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: `${JSON.stringify(meta)}` }}
          />
          <script async src="/intlTelInput.min.js" />

          {faqMeta && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: `${JSON.stringify(faqMeta)}` }}
            />
          )}
        </Head>
        

        <div className="bg-white shadow-inner">
          <div className="max-w-3xl mx-auto px-4 flex flex-col sm:pb-0 lg:max-w-7xl lg:px-8 md:grid md:grid-cols-2">
            <div className="pb-12 lg:py-12 md:flex md:flex-col md:border-r md:border-gray-200 md:py-12 md:pl-0 md:pr-10 lg:pr-16 order-2 md:order-1 text-gray-500 text-lg">
                  <h3 className="text-3xl font-serif font-medium text-gray-900 sm:text-3xl lg:leading-snug">
                    Advice and solutions from our team of expert aestheticians
                  </h3>
              <p>
                Soluna London offers a wide selection of services which cater to
                many concerns and conditions. At your consultation a member of
                our team will review your:
              </p>
              <ul className="mt-4" style={{ listStyle: "inside" }}>
                <li>Skin concerns</li>
                <li>Body concerns</li>
                <li>Current skincare regime</li>
                <li>Current exercise routines and diet</li>
              </ul>
              <p className="mt-4">
                Our consultations are strictly confidential and revolve around
                finding the correct treatment solutions unique for you.
              </p>
              <p className="mt-4">
                We pride ourselves on our industry-leading clinical, care and
                safety protocols and exceptional training programmes, so you can
                trust that you&apos;re in the safest and best hands at Soluna
                London.
              </p>
            </div>
            <div className="py-12 order-1 md:order-2 md:pl-6" id="book">
              <h2 className="font-serif text-4xl mb-12 text-center font-medium">
                Book Your Free Consultation
              </h2>
              <EmbeddedAppointmentSelector
                service={consultation}
                consultation
              />
              <div className="md:max-w-md md:mx-auto">
                <EmbeddedDetails
                  profile={profile}
                  ref={detailsRef}
                  consultation
                />
              </div>

              <div className="md:max-w-md md:mx-auto mb-6">
                {state.error && (
                  <p className="font-medium text-sm text-red-400">
                    {state.error}
                  </p>
                )}
                <button
                  disabled={state.loading}
                  type="button"
                  className="btn-primary relative"
                  onClick={() => handleBooking(consultation)}
                >
                  {!state.loading && <span>Book Your Consultation</span>}
                  {state.loading && <span>Processing</span>}
                  {state.loading && (
                    <span className="absolute right-1">
                      <Spinner className="w-9 h-9" />
                    </span>
                  )}
                </button>
              </div>
              <Link
                  href="/contact/"
                  passHref
                  className="mx-4 flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base text-white bg-brand-600 hover:bg-brand-700"
                >
                Need help? Contact us here
              </Link>
            </div>
          </div>
        </div>

        <nav aria-label="Offers" className="order-last lg:order-first border-t">
          <div className="mx-auto max-w-7xl lg:px-8 border-l border-r">
            <ul
              role="list"
              className="grid grid-cols-1 divide-y divide-gray-200 lg:grid-cols-3 lg:divide-y-0 lg:divide-x"
            >
              {offers.map((offer) => (
                <li key={offer.name} className="flex flex-col">
                  <a
                    href={offer.href}
                    className="relative flex flex-1 flex-col justify-center bg-white py-6 px-4 text-center focus:z-10"
                  >
                    <p className="text-sm text-gray-500">{offer.name}</p>
                    <p className="font-semibold text-gray-900">
                      {offer.description}
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="bg-white">
          <div className="max-w-2xl mx-auto py-6 px-4 sm:pt-24 sm:pb-12 sm:px-6 lg:max-w-7xl lg:px-8 ">
            <div className="lg:grid lg:grid-cols-2 lg:gap-x-8">
              {/* Product details */}
              <div className="lg:max-w-lg lg:self-end">
                <div className="md:hidden lg:col-start-2 lg:row-span-2 lg:self-start lg:pt-6">
                  <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                    <img
                      src="https://solunalondon.com/_next/image/?url=https%3A%2F%2Fs3.eu-west-2.amazonaws.com%2Fassets.charmelle.london%2F1728886901562-Body%20consult.png&w=3840&q=75"
                      className="object-fill w-full rounded-md overflow-hidden"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <h1 className="text-3xl font-serif font-medium text-gray-900 sm:text-5xl lg:leading-snug">
                    Advice and solutions from our team of expert aestheticians
                  </h1>
                </div>

                <section aria-labelledby="information-heading" className="mt-4">
                  <h2 id="information-heading" className="sr-only">
                    {service.title} information
                  </h2>

                  <div className="mt-4 space-y-6">
                    <p className="text-xl text-gray-500">
                      Our team have worked with thousands of clients to help
                      them achieve their skin and aesthetics goals. We offer a
                      free no obligation consultation to discuss your concerns
                      and help you decide on the best treatment plan for you.
                    </p>
                  </div>

                  {service.attributes && service.attributes.ratings && (
                    <div className="hidden md:flex md:items-center md:mt-4">
                      <h2 className="sr-only">Reviews</h2>
                      <div className="flex flex-col md:flex-row lg:items-center">
                        <div className="flex items-center">
                          {[0, 1, 2, 3, 4].map((rating) => (
                            <StarIcon
                              key={`StarIcon-${rating}`}
                              className={classNames(
                                service.attributes.ratings.value > rating
                                  ? "text-brand-400"
                                  : "text-gray-300",
                                "h-5 w-5 flex-shrink-0"
                              )}
                              aria-hidden="true"
                            />
                          ))}
                        </div>
                        <p className="mt-1 lg:mt-0 lg:ml-2 text-base text-gray-500">
                          5 star service from a trusted and experienced team
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-8 md:w-44">
                    <a href="#book" className="btn-primary">
                      Book
                    </a>
                  </div>
                </section>
              </div>

              <div className="hidden lg:block mt-10 lg:mt-0 lg:col-start-2 lg:row-span-2 lg:self-start lg:pt-6">
                <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                  <img
                    src="https://solunalondon.com/_next/image/?url=https%3A%2F%2Fs3.eu-west-2.amazonaws.com%2Fassets.charmelle.london%2F1728886901562-Body%20consult.png&w=3840&q=75"
                    className="object-fill w-full rounded-md overflow-hidden"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <nav
          aria-label="Offers"
          className="order-last lg:order-first border-t border-b"
        >
          <div className="mx-auto max-w-7xl lg:px-8 border-l border-r">
            <ul
              role="list"
              className="grid grid-cols-1 divide-y divide-gray-200 lg:grid-cols-3 lg:divide-y-0 lg:divide-x"
            >
              {offers2.map((offer) => (
                <li key={offer.name} className="flex flex-col">
                  <div className="relative flex flex-1 flex-col justify-center bg-white py-6 px-4 text-center focus:z-10">
                    <p className="text-sm text-gray-500">{offer.name}</p>
                    <p className="font-semibold text-gray-900">
                      {offer.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <section className="bg-brand-800">
          <div className="mx-auto max-w-7xl md:grid md:grid-cols-2 md:px-6 lg:px-8">
            <div className="py-12 px-4 sm:px-6 md:flex md:flex-col md:border-r md:border-brand-900 md:py-16 md:pl-0 md:pr-10 lg:pr-16">
              <q className="mt-6 md:flex md:flex-grow md:flex-col">
                <div className="relative text-lg font-medium text-white md:flex-grow">
                  <svg
                    className="absolute top-0 left-0 h-8 w-8 -translate-x-3 -translate-y-2 transform text-brand-600"
                    fill="currentColor"
                    viewBox="0 0 32 32"
                    aria-hidden="true"
                  >
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                  <p className="relative">
                    Had 3 hydro facials at Soluna London so far and I can&apos;t
                    wait to go back. The facial itself is so relaxing (I almost
                    fall asleep every time) and my skin feels/ looks amazing
                    after but the staff are even better - they are lovely and
                    welcoming and make sure they know exactly what you want out
                    of your experience, can&apos;t recommend Soluna London
                    enough!
                  </p>
                </div>
                <footer className="mt-8">
                  <div className="flex items-center">
                    <div className="inline-flex flex-shrink-0 rounded-full border-2 border-white">
                      <img
                        className="h-12 w-12 rounded-full"
                        src="https://lh3.googleusercontent.com/a-/ACNPEu95cAGbz8neuozrOy4xVVcT-Fwde3SjltfluHm-Cw=s40-c-c0x00000000-cc-rp-mo-br100"
                        alt=""
                      />
                    </div>
                    <div className="ml-4">
                      <div className="text-base font-medium text-white">
                        Jordan O&apos;Driscoll
                      </div>
                    </div>
                  </div>
                </footer>
              </q>
            </div>
            <div className="border-t-2 border-brand-900 py-12 px-4 sm:px-6 md:border-t-0 md:border-l md:py-16 md:pr-0 md:pl-10 lg:pl-16">
              <q className="mt-6 md:flex md:flex-grow md:flex-col">
                <div className="relative text-lg font-medium text-white md:flex-grow">
                  <svg
                    className="absolute top-0 left-0 h-8 w-8 -translate-x-3 -translate-y-2 transform text-brand-600"
                    fill="currentColor"
                    viewBox="0 0 32 32"
                  >
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                  <p className="relative">
                    Have been receiving facial peels from Soluna London for
                    chickenpox scars that left my face in a bad way. The ladies
                    have been lovely! From the consultation to now the
                    treatments. No one tried to upsell me anything and
                    they&apos;ve been incredibly kind. Happy to be going there!
                  </p>
                </div>
                <footer className="mt-8">
                  <div className="flex items-center">
                    <div className="inline-flex flex-shrink-0 rounded-full border-2 border-white">
                      <img
                        className="h-12 w-12 rounded-full"
                        src="https://lh3.googleusercontent.com/a-/ACNPEu9tGp2dco6j3sMwXh2Hsr7oL4ymhCQXibRGFDQx0g=s40-c-c0x00000000-cc-rp-mo-br100"
                        alt=""
                      />
                    </div>
                    <div className="ml-4">
                      <div className="text-base font-medium text-white">
                        Majean G
                      </div>
                    </div>
                  </div>
                </footer>
              </q>
            </div>
          </div>
        </section>

        {/* callback module */}
        <div className="bg-white">
          <div className="max-w-2xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:max-w-7xl">
            <div className="relative rounded-lg overflow-hidden h-96">
              <div className="absolute inset-0">
                <Image
                  src={props.callback.imageUrl}
                  alt={props.callback.body}
                  className="w-full h-full object-center object-cover"
                  fill
                  sizes="100vw"
                />
              </div>
              <div
                aria-hidden="true"
                className="relative w-full h-96 lg:hidden"
              ></div>
              <div
                aria-hidden="true"
                className="relative w-full h-32 lg:hidden"
              ></div>
              <div className="absolute inset-x-0 bottom-0 bg-black bg-opacity-75 p-6 rounded-bl-lg rounded-br-lg backdrop-filter backdrop-blur sm:flex sm:items-center sm:justify-between lg:inset-y-0 lg:inset-x-auto lg:w-96 lg:rounded-tl-lg lg:rounded-br-none lg:flex-col lg:items-start">
                <div>
                  <h2 className="text-xl font-bold text-white lg:text-2xl">
                    {props.callback.heading}
                  </h2>
                  <p className="mt-3 text-sm lg:text-base text-gray-300">
                    {props.callback.body}
                  </p>
                </div>

                <button
                  type="submit"
                  className="btn-primary mt-6 sm:mt-0"
                  onClick={() =>
                    setState({
                      ...state,
                      callback: true,
                    })
                  }
                >
                  <span className="absolute left-0 inset-y pl-3 text-white">
                    <Phone className="h-5 w-5 text-white group-hover:text-brand-400 transition ease-in-out duration-150 fill-current" />
                  </span>
                  {props.callback.ctaText}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* klarna module */}
        <div className="px-8 bg-black relative z-0 mb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* @ts-ignore */}
            <Klarna
              data-key="homepage-promotion-box"
              data-locale="en-GB"
              data-theme="dark"
            >
              {/* @ts-ignore */}
            </Klarna>
          </div>
        </div>
        {props.banner && (
          <div className="relative bg-white overflow-hidden md:overflow-visible">
            <div className="pt-16 pb-96 sm:pt-24 sm:pb-40 lg:pt-20 lg:pb-48">
              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 sm:static">
                <div className="sm:max-w-lg">
                  <div>
                    <Markdown
                      source={props.banner.body}
                      renderers={{
                        h3: (props) => (
                          <h3 className="font-serif text-4xl font font-medium tracking-tight text-gray-900 sm:text-6xl">
                            {props.children}
                          </h3>
                        ),
                        h4: (props) => (
                          <h4 className="font-serif text-4xl font font-medium tracking-tight text-gray-900 sm:text-6xl">
                            {props.children}
                          </h4>
                        ),
                      }}
                    />
                  </div>
                  <Link
                    href={props.banner.link}
                    passHref
                    className="inline-block text-center bg-brand-600 border border-transparent rounded-md py-3 px-8 font-medium text-white hover:bg-brand-700 font-serif mt-6 text-xl"
                  >
                    {props.banner.ctaText}
                  </Link>
                </div>
                <div>
                  <div className="mt-10">
                    <div
                      aria-hidden="true"
                      className="pointer-events-none lg:absolute lg:inset-y-0 lg:max-w-7xl lg:mx-auto lg:w-full"
                    >
                      <div className="absolute transform sm:left-1/2 sm:top-12 sm:translate-x-8 lg:left-1/2 lg:top-1/2 lg:-translate-y-1/2 lg:translate-x-8">
                        <div className="flex items-center space-x-6 lg:space-x-8">
                          <div className="flex-shrink-0 grid grid-cols-1 gap-y-6 lg:gap-y-8">
                            <div className="relative w-44 h-64 rounded-lg overflow-hidden sm:opacity-0 lg:opacity-100  md:shadow-lg">
                              <Image
                                className="w-full h-full object-center object-cover"
                                src={props.banner.imagesArray[0]}
                                alt=""
                                fill
                                sizes="100vw"
                              />
                            </div>
                            <div className="relative w-44 h-64 rounded-lg overflow-hidden  md:shadow-lg">
                              <Image
                                className="w-full h-full object-center object-cover"
                                src={props.banner.imagesArray[1]}
                                alt=""
                                fill
                                sizes="100vw"
                              />
                            </div>
                          </div>
                          <div className="flex-shrink-0 grid grid-cols-1 gap-y-6 lg:gap-y-8">
                            <div className="relative w-44 h-64 rounded-lg overflow-hidden  md:shadow-lg">
                              <Image
                                className="w-full h-full object-center object-cover"
                                src={props.banner.imagesArray[3]}
                                alt=""
                                fill
                                sizes="100vw"
                              />
                            </div>
                            <div className="relative w-44 h-64 rounded-lg overflow-hidden  md:shadow-lg">
                              <Image
                                className="w-full h-full object-center object-cover"
                                src={props.banner.imagesArray[4]}
                                alt=""
                                fill
                                sizes="100vw"
                              />
                            </div>
                          </div>
                          <div className="flex-shrink-0 grid grid-cols-1 gap-y-6 lg:gap-y-8">
                            <div className="relative w-44 h-64 rounded-lg overflow-hidden  md:shadow-lg">
                              <Image
                                className="w-full h-full object-center object-cover"
                                src={props.banner.imagesArray[5]}
                                alt=""
                                fill
                                sizes="100vw"
                              />
                            </div>
                            <div className="relative w-44 h-64 rounded-lg overflow-hidden  md:shadow-lg">
                              <Image
                                className="w-full h-full object-center object-cover"
                                src={props.banner.imagesArray[2]}
                                alt=""
                                fill
                                sizes="100vw"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {props.related ? (
          <div className="bg-white">
            <div className="pt-4 pb-16 md:py-16 sm:py-24 xl:max-w-7xl xl:mx-auto xl:px-8">
              <div className="px-4 sm:px-6 sm:flex sm:items-center sm:justify-between lg:px-8 xl:px-0">
                <h2 className="text-2xl font-extrabold tracking-tight text-gray-900">
                  {props.relatedTitle}
                </h2>
                <Link
                  href={props.relatedLink}
                  passHref
                  className="hidden text-sm font-semibold text-brand-600 hover:text-brand-500 sm:block"
                >
                  {props.relatedLinkText}
                  <span aria-hidden="true"> &rarr;</span>
                </Link>
              </div>

              <div className="mt-4 flow-root">
                <div className="-my-2">
                  <div className="box-content py-2 relative h-80 overflow-x-auto xl:overflow-visible">
                    <div className="absolute min-w-screen-xl px-4 flex space-x-8 sm:px-6 lg:px-8 xl:relative xl:px-0 xl:space-x-0 xl:grid xl:grid-cols-5 xl:gap-x-8">
                      {props.related.map((category) => (
                        <Link
                          href={category.href}
                          passHref
                          key={category.name}
                          className="relative w-56 h-80 rounded-lg p-6 flex flex-col overflow-hidden hover:opacity-75 xl:w-auto"
                        >
                          <span aria-hidden="true" className="absolute inset-0">
                            <Image
                              src={category.imageSrc}
                              alt={category.name}
                              className="w-full h-full object-center object-cover"
                              fill
                              sizes="100vw"
                            />
                          </span>
                          <span
                            aria-hidden="true"
                            className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-gray-800 opacity-70"
                          />
                          <span className="relative mt-auto text-center text-xl font-medium font-serif text-white">
                            {category.name}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 px-4 sm:hidden">
                <Link
                  href={props.relatedLink}
                  passHref
                  className="block text-sm font-semibold text-brand-600 hover:text-brand-500"
                >
                  {props.relatedLinkText}
                  <span aria-hidden="true"> &rarr;</span>
                </Link>
              </div>
            </div>
          </div>
        ) : null}
      </Layout>
      <Modal
        open={state.callback}
        close={() => setState({ ...state, callback: false })}
      >
        <div className="w-72 mx-auto">
          <form onSubmit={handleSubmit(handle)} className="space-y-3">
            <h3 className="font-serif text-xl text-center">
              Request a callback
            </h3>
            <label htmlFor="name" className="block">
              <input
                id="name"
                name="first_name"
                type="text"
                placeholder="Enter your name"
                className="appearance-none relative block w-full px-4 py-4 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:shadow-outline-brand focus:z-10 sm:text-sm sm:leading-5 focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:placeholder-gray-400"
                {...register("first_name", { required: true })}
              />
            </label>
            {errors.first_name && (
              <p className="text-red-500">Name is required</p>
            )}
            <label htmlFor="surname" className="block">
              <input
                id="surname"
                name="last_name"
                type="text"
                placeholder="Enter your surname"
                className="appearance-none relative block w-full px-4 py-4 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:shadow-outline-brand focus:z-10 sm:text-sm sm:leading-5 focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:placeholder-gray-400"
                {...register("last_name", { required: true })}
              />
            </label>
            {errors.last_name && (
              <p className="text-red-500">Surname is required</p>
            )}
            <label htmlFor="phone" className="block">
              <input
                id="phone"
                name="phone"
                type="tel"
                placeholder="Enter your phone number"
                className="appearance-none relative block w-full px-4 py-4 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:shadow-outline-brand focus:z-10 sm:text-sm sm:leading-5 focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:placeholder-gray-400"
                {...register("phone", { required: true })}
              />
            </label>
            {errors.phone && (
              <p className="text-red-500">Phone number is required</p>
            )}
            <label htmlFor="email" className="block">
              <input
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                className="appearance-none relative block w-full px-4 py-4 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:shadow-outline-brand focus:z-10 sm:text-sm sm:leading-5 focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:placeholder-gray-400"
                {...register("email", { required: true })}
              />
            </label>
            {errors.email && <p className="text-red-500">Email is required</p>}
            <button
              type="submit"
              className="btn-primary"
              disabled={state.loading}
            >
              Submit
            </button>
          </form>
        </div>
      </Modal>
      <Modal
        open={state.success}
        close={() => setState({ ...state, success: false })}
      >
        <div className="w-72 mx-auto">
          <h3 className="font-serif text-xl text-center">
            Your details have been submitted!
          </h3>
          <p>
            Thank you for your interest in Soluna London. We will be in touch
            with you by phone or email shortly.
          </p>
        </div>
      </Modal>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (context) => {
  const client = ApiClient();

  let facialContent = null;
  let bodyContent = null;

  try {
    const {
      data: { content: x = {} },
    } = await client.query({
      query: serviceByUuid,
      variables: {
        id: "dde4c697-87e6-4d26-a8ca-48d433bd4e1f",
      },
    });
    facialContent = x;

    const {
      data: { content: y = {} },
    } = await client.query({
      query: serviceByUuid,
      variables: {
        id: "147ca6ad-1a55-40b4-af5c-00facd5c154b",
      },
    });
    bodyContent = y;
  } catch (e) {
    Sentry.captureException(e);
  }

  return {
    revalidate: 60 * 60,
    props: {
      callback: {
        heading: "Request a callback from one of our experts",
        body: "We offer in-depth, comprehensive consultations with an experienced specialist to answer all of your questions and give you expert advice when considering treatments at Soluna London",
        ctaText: "Request A Callback",
        imageUrl: "/video-poster.png",
      },
      variant: {
        id: "consultation",
        version: 1,
      },
      relatedTitle: "Popular Services",
      relatedLink: "/treatments/face/",
      relatedLinkText: "Browse facials",
      related: [
        {
          name: "Hydrofacial",
          href: "/hydrofacial/",
          imageSrc:
            "https://solunalondon.com/_next/image/?url=%2Fservices%2Ffacials%2Fhydro2facial-action.jpg&w=3840&q=75",
        },
        {
          name: "Hollywood Facial",
          href: "/hollywood-facial/",
          imageSrc:
            "https://solunalondon.com/_next/image/?url=%2Fservices%2Ffacials%2Fmillion-dollar-facial.jpg&w=3840&q=75",
        },
        {
          name: "Vampire",
          href: "/dracula-skin-therapy/",
          imageSrc:
            "https://solunalondon.com/_next/image/?url=%2Fservices%2Ffacials%2Fvampire_facial.jpg&w=3840&q=75",
        },
        {
          name: "HiFu",
          href: "/hifu/",
          imageSrc:
            "https://solunalondon.com/_next/image/?url=%2Fservices%2Ffacials%2Fhifu.jpg&w=3840&q=75",
        },
        {
          name: "Lip Enhancement",
          href: "/lip-enhancement/",
          imageSrc: "/images/lip-enhancements-london.png",
        },
      ],
      path: "consultation",
      facialContent: facialContent ? facialContent : null,
      bodyContent: bodyContent ? bodyContent : null,
    },
  };
};
